/**
 * DispositionModal — the returns-disposition decision dialog (Cluster 8, Architecture Rule 10).
 * Opened from the Returns Inspection / Disposition queues (admin/user only). Lets the operator
 * pick a disposition outcome for a return and submit it; the parent owns the submit_disposition
 * RPC call (see useReturnDisposition). Each outcome's consequence is surfaced inline so the
 * money/inventory effect is explicit before submitting.
 */
import { useState, useEffect, type JSX } from 'react';
import { X } from 'lucide-react';
import ArbiSelect from '../ui/ArbiSelect';

export interface DispositionTarget {
  id: string;
  return_number?: string | null;
  isg_sku?: string | null;
  title?: string | null;
  quantity?: number | null;
  condition_received?: string | null;
  disposition?: string | null;
}

const DISPOSITION_OPTIONS: { value: string; label: string }[] = [
  { value: 'relist_new', label: 'Relist (New)' },
  { value: 'relist_used', label: 'Relist (Used)' },
  { value: 'relist_open_box', label: 'Relist (Open Box)' },
  { value: 'send_to_fba', label: 'Send to FBA' },
  { value: 'return_to_distributor', label: 'Return to Distributor' },
  { value: 'dispose', label: 'Dispose' },
  { value: 'write_off', label: 'Write Off' },
  { value: 'dispute_marketplace', label: 'Dispute (Marketplace)' },
];

// Consequence disclosure — what each outcome does to inventory + finance.
const OUTCOME_EFFECT: Record<string, string> = {
  relist_new: 'Returns the unit to sellable stock and opens a Listings task.',
  relist_used: 'Returns the unit to sellable stock and opens a Listings task.',
  relist_open_box: 'Returns the unit to sellable stock and opens a Listings task.',
  send_to_fba: 'Creates an inbound transfer and stocks the unit at FBA-inbound.',
  return_to_distributor: 'Creates a removal transfer and a Transfers task — the goods ship back.',
  dispose: 'Records a finance write-off at cost. The unit is not returned to stock.',
  write_off: 'Records a finance write-off at cost. The unit is not returned to stock.',
  dispute_marketplace:
    'Records the dispute only — no inventory or finance change until it resolves.',
};

interface DispositionModalProps {
  open: boolean;
  item: DispositionTarget | null;
  onConfirm: (outcome: string, notes: string) => void;
  onCancel: () => void;
  submitting: boolean;
  error?: string | null;
}

export default function DispositionModal({
  open,
  item,
  onConfirm,
  onCancel,
  submitting,
  error,
}: DispositionModalProps): JSX.Element | null {
  const [outcome, setOutcome] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  useEffect(() => {
    if (open) {
      setOutcome('');
      setNotes('');
    }
  }, [open, item?.id]);

  if (!open || !item) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <button
        type="button"
        className="absolute inset-0 bg-black/60 cursor-default"
        onClick={onCancel}
        aria-label="Close dialog"
        tabIndex={-1}
      />
      <div
        className="relative bg-surface-2 border border-border rounded-xl w-[440px] shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Set Disposition"
      >
        <div className="px-5 py-4 border-b border-border-subtle flex items-center justify-between">
          <h3 className="text-sm font-semibold text-ink-bright">
            Set Disposition — {item.return_number || item.isg_sku || 'Return'}
          </h3>
          <button
            onClick={onCancel}
            className="text-ink-muted hover:text-ink-primary transition-colors cursor-pointer"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div className="bg-surface-3 rounded-lg p-3 space-y-1.5">
            <div className="flex justify-between text-xs">
              <span className="text-ink-muted">SKU</span>
              <span className="font-mono text-ink-bright">{item.isg_sku || '—'}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-ink-muted">Quantity</span>
              <span className="font-mono text-ink-bright">{item.quantity ?? '—'}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-ink-muted">Condition</span>
              <span className="text-ink-bright">{item.condition_received || '—'}</span>
            </div>
          </div>

          <div>
            <label
              htmlFor="disposition-outcome"
              className="text-xs font-medium text-ink-secondary mb-1 block"
            >
              Disposition
            </label>
            <div id="disposition-outcome">
              <ArbiSelect
                value={outcome}
                onChange={(v) => setOutcome(v)}
                options={DISPOSITION_OPTIONS}
                placeholder="Select disposition…"
              />
            </div>
            {outcome && OUTCOME_EFFECT[outcome] && (
              <p className="mt-1.5 text-2xs text-ink-muted">{OUTCOME_EFFECT[outcome]}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="disposition-notes"
              className="text-xs font-medium text-ink-secondary mb-1 block"
            >
              Notes (optional)
            </label>
            <textarea
              id="disposition-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="arbi-input w-full text-xs resize-none min-h-[72px]"
              placeholder="Additional notes…"
            />
          </div>

          {error && <p className="text-2xs text-danger">{error}</p>}
        </div>
        <div className="px-5 py-3 border-t border-border-subtle flex justify-end gap-2">
          <button onClick={onCancel} className="arbi-btn-ghost text-xs" disabled={submitting}>
            Cancel
          </button>
          <button
            onClick={() => onConfirm(outcome, notes)}
            className="arbi-btn-primary text-xs"
            disabled={submitting || !outcome}
          >
            {submitting ? 'Saving…' : 'Save Disposition'}
          </button>
        </div>
      </div>
    </div>
  );
}
